export enum GraphhopperErrorType {
  ConnectionNotFound = 'GraphhopperConnectionNotFound',
  IllegalArgument = 'GraphhopperIllegalArgument',
  PointDistanceExceeded = 'GraphhopperPointDistanceExceeded',
  PointNotFound = 'GraphhopperPointNotFound',
  PointOutOfBounds = 'GraphhopperPointOutOfBounds',
  RateLimitExceeded = 'GraphhopperRateLimitExceeded',
  RuntimeError = 'GraphhopperRuntimeError',
  ServiceUnavailable = 'GraphhopperServiceUnavailable',
  TooManyPoints = 'GraphhopperTooManyPoints',
}

export enum GraphhopperExceptionDetail {
  ConnectionNotFoundException = 'com.graphhopper.util.exceptions.ConnectionNotFoundException',
  DetailedIllegalArgumentException = 'com.graphhopper.util.exceptions.DetailedIllegalArgumentException',
  DetailedRuntimeException = 'com.graphhopper.util.exceptions.DetailedRuntimeException',
  PointDistanceExceededException = 'com.graphhopper.util.exceptions.PointDistanceExceededException',
  PointNotFoundException = 'com.graphhopper.util.exceptions.PointNotFoundException',
  PointOutOfBoundsException = 'com.graphhopper.util.exceptions.PointOutOfBoundsException',
}

export interface GraphhopperRateLimitInfo {
  limit: number;
  remaining: number;
  reset: number;
  credits: number;
}

export interface GraphhopperErrorHint {
  message: string;
  details: string;
  point_index?: number;
}

export interface GraphhopperErrorResponse {
  message: string;
  hints?: GraphhopperErrorHint[];
}

export enum RouteErrorType {
  ConnectionNotFound = 'ConnectionNotFound',
  IllegalArgument = 'IllegalArgument',
  NotEnoughPoints = 'NotEnoughPoints',
  PointDistanceExceeded = 'PointDistanceExceeded',
  PointNotFound = 'PointNotFound',
  PointOutOfBounds = 'PointOutOfBounds',
  RateLimitExceeded = 'RateLimitExceeded',
  RuntimeError = 'RuntimeError',
  ServiceUnavailable = 'ServiceUnavailable',
  TooManyPoints = 'TooManyPoints',
  Unknown = 'Unknown',
}

export interface RouteErrorData {
  error: GraphhopperErrorType | RouteErrorType;
  maxAllowedPoints?: number;
  message: string;
  pointCount?: number;
  pointIndex?: number;
  rateLimit?: GraphhopperRateLimitInfo;
}

export class RouteError extends Error {
  errorType: RouteErrorType;
  maxAllowedPoints?: number;
  pointCount?: number;
  pointIndex?: number;
  rateLimit?: GraphhopperRateLimitInfo;
  showRetryButton?: boolean;

  constructor(message: string, errorType: RouteErrorType, options?: {
    maxAllowedPoints?: number;
    pointCount?: number;
    pointIndex?: number;
    rateLimit?: GraphhopperRateLimitInfo;
    showRetryButton?: boolean;
  }) {
    super(message);
    this.errorType = errorType;
    this.maxAllowedPoints = options?.maxAllowedPoints;
    this.name = 'RouteError';
    this.pointCount = options?.pointCount;
    this.pointIndex = options?.pointIndex;
    this.rateLimit = options?.rateLimit;
    this.showRetryButton = options?.showRetryButton;
  }
}

/**
 * Maps GraphhopperErrorType to RouteErrorType
 */
export function mapGraphhopperErrorToRouteError(graphhopperError: GraphhopperErrorType): RouteErrorType {
  switch (graphhopperError) {
    case GraphhopperErrorType.ConnectionNotFound:
      return RouteErrorType.ConnectionNotFound;
    case GraphhopperErrorType.IllegalArgument:
      return RouteErrorType.IllegalArgument;
    case GraphhopperErrorType.PointDistanceExceeded:
      return RouteErrorType.PointDistanceExceeded;
    case GraphhopperErrorType.PointNotFound:
      return RouteErrorType.PointNotFound;
    case GraphhopperErrorType.PointOutOfBounds:
      return RouteErrorType.PointOutOfBounds;
    case GraphhopperErrorType.RuntimeError:
      return RouteErrorType.RuntimeError;
    case GraphhopperErrorType.RateLimitExceeded:
      return RouteErrorType.RateLimitExceeded;
    case GraphhopperErrorType.ServiceUnavailable:
      return RouteErrorType.ServiceUnavailable;
    case GraphhopperErrorType.TooManyPoints:
      return RouteErrorType.TooManyPoints;
    default:
      return RouteErrorType.Unknown;
  }
}

/**
 * Determines if a retry button should be shown based on the error type
 */
function shouldShowRetryButton(errorType: RouteErrorType): boolean {
  switch (errorType) {
    case RouteErrorType.RateLimitExceeded:
    case RouteErrorType.ServiceUnavailable:
      return true;
    default:
      return false;
  }
}

/**
 * Creates a RouteError from an API error response
 */
export function createRouteErrorFromApiResponse(error: unknown): RouteError {
  // Check if it's an axios error with response data
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: Partial<RouteErrorData> } };
    const errorData = axiosError.response?.data;

    if (errorData?.error) {
      const errorType = typeof errorData.error === 'string'
        ? mapGraphhopperErrorToRouteError(errorData.error as GraphhopperErrorType)
        : RouteErrorType.Unknown;

      return new RouteError(
        errorData.message ?? 'Error getting route',
        errorType,
        {
          maxAllowedPoints: errorData.maxAllowedPoints,
          pointCount: errorData.pointCount,
          pointIndex: errorData.pointIndex,
          rateLimit: errorData.rateLimit,
          showRetryButton: shouldShowRetryButton(errorType),
        },
      );
    }
  }

  // Fallback for unknown errors
  return new RouteError(
    error instanceof Error ? error.message : 'Unknown error occurred',
    RouteErrorType.Unknown,
  );
}
